<script lang="ts">
    import type {UserAccountReactivePageData, UserAccountStaticPageData} from '../types';
    import type {DelayedReturnPenalty} from '../../types';
    import {pipe} from 'core/utils';
    import {loc, priceFormatter} from 'shared/utils/pipes';
    import {exists} from 'shared/utils/custom-utils';
    import {getCurrentLanguage, getDateFormatter, getLocalization} from 'core/svelte-context/context';
    import {getPageContext} from 'shared/layouts/page-context';
    import {onDestroy} from 'svelte';
    import KpGenericPanel from 'shared/ui-widgets/panel/KpGenericPanel.svelte';

    const localize = getLocalization();
    const dateFormatter = getDateFormatter();
    const pageContext = getPageContext<UserAccountStaticPageData, UserAccountReactivePageData>();

    let delayedReturnPenalty: DelayedReturnPenalty | undefined;
    const reactiveDataUnsubscribe = pageContext.reactiveData.subscribe((data) => delayedReturnPenalty = data.delayedReturnPenalty);

    const user = pageContext.staticData.user;
    const detailView = pageContext.staticData.detailView;
    const readerRole = user.readerAccounts[0];

    onDestroy(reactiveDataUnsubscribe);
</script>

<KpGenericPanel additionalClasses="kp-user-account-information-part"
                hasBodyPadding="{false}">

    <span slot="heading">{localize(/* @kp-localization konto.UserInformation */ 'konto.UserInformation')}</span>

    <div class="information-container">
        {#if exists(readerRole?.readerCategory)}
            <div class="information-row">
                {localize(/* @kp-localization ctenar.readerCategory */ 'ctenar.readerCategory')}
                <span class="value">{pipe(readerRole.readerCategory, loc())}</span>
            </div>
        {/if}

        {#if exists(readerRole?.cardNumber)}
            <div class="information-row">
                {localize(/* @kp-localization ctenar.cardNumber */ 'ctenar.cardNumber')}
                <span class="value">{pipe(readerRole.cardNumber, loc())}</span>
            </div>
        {/if}

        {#if exists(readerRole?.barCode)}
            <div class="information-row">
                {localize(/* @kp-localization ctenar.barCode */ 'ctenar.barCode')}
                <span class="value">{pipe(readerRole.barCode, loc())}</span>
            </div>
        {/if}

        {#if exists(readerRole?.registrationDate)}
            <div class="information-row">
                {localize(/* @kp-localization konto.datumRegistrace */ 'konto.datumRegistrace')}
                <span class="value">{pipe(readerRole.registrationDate, dateFormatter('d.M.yyyy'))}</span>
            </div>
        {/if}

        {#if exists(readerRole?.registrationExpirationDate)}
            <div class="information-row">
                {localize(/* @kp-localization konto.datumVyprseniPlatnostiRegistrace */ 'konto.datumVyprseniPlatnostiRegistrace')}
                <span data-qa="reader-expirationDate-value" class="value">
                    {pipe(readerRole.registrationExpirationDate, dateFormatter('d.M.yyyy'))}
                </span>
            </div>
        {/if}

        {#if exists(user.schoolClass)}
            <div class="information-row">
                {localize(/* @kp-localization ctenar.schoolClass */ 'ctenar.schoolClass')}
                <span class="value">{user.schoolClass}</span>
            </div>
        {/if}

        {#if detailView.canUsersShow && readerRole?.note}
            <div class="information-row">
                {localize(/* @kp-localization ctenar.note */ 'ctenar.note')}
                <span class="value">{readerRole.note}</span>
            </div>
        {/if}

        {#if exists(delayedReturnPenalty) && delayedReturnPenalty.penalizedLoansCount > 0 && delayedReturnPenalty.price.amount > 0}
            <div class="information-row">
                {localize(/* @kp-localization konto.SoucetPokutZaAktualniVypujcky */ 'konto.SoucetPokutZaAktualniVypujcky')}
                <span class="value">{pipe(delayedReturnPenalty.price, priceFormatter(getCurrentLanguage()))}</span>
            </div>
        {/if}

        {#each user.editorAccounts as editorAccount}
            {#if exists(editorAccount.editLevel)}
                <div class="information-row">
                    {localize(/* @kp-localization user.LibrarianEditLevel */ 'user.LibrarianEditLevel')}
                    <span class="value">{pipe(editorAccount.editLevel, loc())}</span>
                </div>
            {/if}

            {#if exists(editorAccount.group)}
                <div class="information-row">
                    {localize(/* @kp-localization user.UserGroup */ 'user.UserGroup')}
                    <span class="value">{pipe(editorAccount.group, loc())}</span>
                </div>
            {/if}

            {#if exists(editorAccount.validationCode)}
                <div class="information-row">
                    {localize(/* @kp-localization user.ValidationCode */ 'user.ValidationCode')}
                    <span class="value">{pipe(editorAccount.validationCode, loc())}</span>
                </div>
            {/if}
        {/each}
    </div>
</KpGenericPanel>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .information-container {
        display: flex;
        flex-direction: column;

        .information-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid @themed-border-default;
            gap: @spacing-m;
            padding: @spacing-base @spacing-ml;

            &:last-child {
                border-bottom: none;
            }

            .value {
                text-align: end;
                font-weight: 500;
            }
        }
    }
</style>